// /var/www/kikai/src/components/SystemHealth.tsx

import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
  Server, Database, Activity, Clock, Download, Upload, RefreshCw, 
  CheckCircle, XCircle, AlertTriangle, Calendar, HardDrive, Shield,
  Archive, Trash2, Eye, Play, RotateCcw, FileText, TrendingUp
} from 'lucide-react';

interface Version {
  id: number;
  version: string;
  release_date: string;
  release_type: 'major' | 'minor' | 'patch' | 'hotfix';
  changelog_es: string;
  changelog_en: string;
  modules_affected: string[];
  author: string;
  git_commit_hash: string | null;
  is_active: boolean;
  created_at: string;
}

interface Backup {
  id: number;
  version_id: number;
  version: string;
  release_type: string;
  backup_type: 'full_db' | 'incremental_db' | 'files' | 'complete';
  file_path: string;
  file_size_mb: number;
  checksum_sha256: string;
  encryption_enabled: boolean;
  encryption_algorithm: string;
  status: 'success' | 'failed' | 'partial' | 'in_progress';
  error_message: string | null;
  retention_until: string;
  created_at: string;
}

interface BackupStats {
  totalBackups: number;
  successfulBackups: number;
  totalSizeMb: number;
  lastBackup: Backup | null;
}

const SystemHealth: React.FC = () => {
  const { isDark } = useTheme();
  
  const [currentVersion, setCurrentVersion] = useState<Version | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [backupStats, setBackupStats] = useState<BackupStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'versions' | 'backups'>('overview');
  const [expandedChangelog, setExpandedChangelog] = useState<number | null>(null);
  const [selectedBackupType, setSelectedBackupType] = useState<string>('full_db');
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [language, setLanguage] = useState<'es' | 'en'>('es');

  // Clases dinámicas para tema
  const bgClass = isDark ? 'bg-gray-900' : 'bg-gray-50';
  const cardClass = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textClass = isDark ? 'text-gray-100' : 'text-gray-800';
  const textSecondaryClass = isDark ? 'text-gray-400' : 'text-gray-600';
  const borderClass = isDark ? 'border-gray-700' : 'border-gray-200';
  const inputClass = isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCurrentVersion(),
        loadVersions(),
        loadBackups(),
        loadBackupStats()
      ]);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentVersion = async () => {
    try {
      const response = await fetch('/api/system/version');
      const data = await response.json();
      if (data.success) {
        setCurrentVersion(data.data);
      }
    } catch (error) {
      console.error('Error cargando versión actual:', error);
    }
  };

const loadVersions = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch('/api/system/versions?limit=10', {
      headers: {
        'Authorization': 'Bearer ' + token
      }
    });
    const data = await response.json();
    if (data.success) {
      setVersions(data.data.data);
    }
  } catch (error) {
    console.error('Error cargando versiones:', error);
  }
};

const loadBackups = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch('/api/system/backups?limit=10', {
      headers: {
        'Authorization': 'Bearer ' + token
      }
    });
    const data = await response.json();
    if (data.success) {
      setBackups(data.data.data);
    }
  } catch (error) {
    console.error('Error cargando backups:', error);
  }
};

const loadBackupStats = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch('/api/system/backups/stats', {
      headers: {
        'Authorization': 'Bearer ' + token
      }
    });
    const data = await response.json();
    if (data.success) {
      setBackupStats(data.data);
    }
  } catch (error) {
    console.error('Error cargando estadísticas:', error);
  }
};

const createBackup = async () => {
  if (isCreatingBackup) return;

  setIsCreatingBackup(true);
  try {
    const token = localStorage.getItem('token');
    const response = await fetch('/api/system/backups', {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        backupType: selectedBackupType,
        skipEncryption: false
      })
    });

    const data = await response.json();
    
    if (data.success) {
      alert('✅ Backup creado exitosamente: ' + data.data.file_path);
      await loadBackups();
      await loadBackupStats();
    } else {
      alert('❌ Error: ' + data.error);
    }
  } catch (error) {
    console.error('Error creando backup:', error);
    alert('❌ Error al crear backup');
  } finally {
    setIsCreatingBackup(false);
  }
};

const restoreBackup = async (id: number, dryRun: boolean = false) => {
  const confirmMessage = dryRun
    ? '¿Validar este backup sin aplicar cambios?'
    : '⚠️ ADVERTENCIA: Esta acción restaurará el sistema. ¿Continuar?';

  if (!confirm(confirmMessage)) return;

  try {
    const token = localStorage.getItem('token');
    const url = '/api/system/backups/' + id + '/restore';
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        validateChecksum: true,
        dryRun: dryRun
      })
    });

    const data = await response.json();
    
    if (data.success) {
      alert('✅ ' + data.message);
    } else {
      alert('❌ Error: ' + data.error);
    }
  } catch (error) {
    console.error('Error restaurando backup:', error);
    alert('❌ Error al restaurar backup');
  }
};

const cleanupExpiredBackups = async () => {
  if (!confirm('¿Eliminar todos los backups expirados?')) return;

  try {
    const token = localStorage.getItem('token');
    const response = await fetch('/api/system/backups/cleanup', {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + token
      }
    });

    const data = await response.json();
    
    if (data.success) {
      alert('✅ ' + data.message);
      await loadBackups();
      await loadBackupStats();
    } else {
      alert('❌ Error: ' + data.error);
    }
  } catch (error) {
    console.error('Error limpiando backups:', error);
    alert('❌ Error al limpiar backups');
  }
};

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (sizeMb: number) => {
    if (sizeMb < 1) return (sizeMb * 1024).toFixed(2) + ' KB';
    if (sizeMb < 1024) return sizeMb.toFixed(2) + ' MB';
    return (sizeMb / 1024).toFixed(2) + ' GB';
  };

  const getBackupTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'full_db': 'Base de Datos Completa',
      'incremental_db': 'Base de Datos Incremental',
      'files': 'Archivos',
      'complete': 'Completo (DB + Archivos)'
    };
    return labels[type] || type;
  };

  const getReleaseTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      'major': 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900 dark:text-red-200 dark:border-red-700',
      'minor': 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700',
      'patch': 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-200 dark:border-green-700',
      'hotfix': 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900 dark:text-orange-200 dark:border-orange-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'in_progress': return <Clock className="w-5 h-5 text-blue-600 animate-spin" />;
      default: return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${bgClass} ${textClass}`}>
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-lg">Cargando información del sistema...</span>
      </div>
    );
  }

  return (
    <div className={`p-6 max-w-7xl mx-auto ${bgClass} min-h-screen`}>
      {/* Header */}
      <div className="mb-6">
        <h1 className={`text-3xl font-bold ${textClass} flex items-center gap-3`}>
          <Server className="w-8 h-8 text-blue-600" />
          Salud del Sistema
        </h1>
        <p className={`${textSecondaryClass} mt-2`}>
          Monitoreo de versiones, respaldos y estado del sistema KIKAI
        </p>
      </div>

      {/* Tabs */}
      <div className={`flex gap-2 mb-6 border-b ${borderClass}`}>
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'overview'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : textSecondaryClass + ' hover:' + textClass
          }`}
        >
          <Activity className="w-5 h-5 inline mr-2" />
          Resumen
        </button>
        <button
          onClick={() => setActiveTab('versions')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'versions'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : textSecondaryClass + ' hover:' + textClass
          }`}
        >
          <FileText className="w-5 h-5 inline mr-2" />
          Versiones
        </button>
        <button
          onClick={() => setActiveTab('backups')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'backups'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : textSecondaryClass + ' hover:' + textClass
          }`}
        >
          <Archive className="w-5 h-5 inline mr-2" />
          Respaldos
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Versión Actual */}
          <div className={`${cardClass} rounded-lg shadow-md p-6 border-l-4 border-blue-600`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-xl font-bold ${textClass} flex items-center gap-2`}>
                <TrendingUp className="w-6 h-6 text-blue-600" />
                Versión Actual
              </h2>
              <span className="text-3xl font-bold text-blue-600">
                {currentVersion?.version || 'N/A'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className={textSecondaryClass}>Tipo de release:</span>
                <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold border ${
                  currentVersion ? getReleaseTypeBadge(currentVersion.release_type) : ''
                }`}>
                  {currentVersion?.release_type || 'N/A'}
                </span>
              </div>
              <div>
                <span className={textSecondaryClass}>Fecha:</span>
                <span className={`ml-2 font-medium ${textClass}`}>
                  {currentVersion ? formatDate(currentVersion.release_date) : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Estadísticas de Backups */}
          {backupStats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className={`${cardClass} rounded-lg shadow-md p-6 border`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`${textSecondaryClass} text-sm`}>Total Backups</p>
                    <p className={`text-2xl font-bold ${textClass}`}>{backupStats.totalBackups}</p>
                  </div>
                  <Archive className="w-12 h-12 text-blue-600 opacity-20" />
                </div>
              </div>

              <div className={`${cardClass} rounded-lg shadow-md p-6 border`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`${textSecondaryClass} text-sm`}>Backups Exitosos</p>
                    <p className="text-2xl font-bold text-green-600">{backupStats.successfulBackups}</p>
                  </div>
                  <CheckCircle className="w-12 h-12 text-green-600 opacity-20" />
                </div>
              </div>

              <div className={`${cardClass} rounded-lg shadow-md p-6 border`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`${textSecondaryClass} text-sm`}>Espacio Utilizado</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatFileSize(backupStats.totalSizeMb)}
                    </p>
                  </div>
                  <HardDrive className="w-12 h-12 text-purple-600 opacity-20" />
                </div>
              </div>
            </div>
          )}

          {/* Último Backup */}
          {backupStats?.lastBackup && (
            <div className={`${cardClass} rounded-lg shadow-md p-6 border`}>
              <h3 className={`text-lg font-bold ${textClass} mb-4 flex items-center gap-2`}>
                <Clock className="w-5 h-5 text-green-600" />
                Último Backup Exitoso
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className={textSecondaryClass}>Tipo</p>
                  <p className={`font-medium ${textClass}`}>{getBackupTypeLabel(backupStats.lastBackup.backup_type)}</p>
                </div>
                <div>
                  <p className={textSecondaryClass}>Tamaño</p>
                  <p className={`font-medium ${textClass}`}>{formatFileSize(backupStats.lastBackup.file_size_mb)}</p>
                </div>
                <div>
                  <p className={textSecondaryClass}>Fecha</p>
                  <p className={`font-medium ${textClass}`}>{formatDate(backupStats.lastBackup.created_at)}</p>
                </div>
                <div>
                  <p className={textSecondaryClass}>Versión</p>
                  <p className={`font-medium ${textClass}`}>{backupStats.lastBackup.version}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Versions Tab */}
      {activeTab === 'versions' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className={`text-xl font-bold ${textClass}`}>Historial de Versiones</h2>
            <button
              onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
              className={`px-4 py-2 ${inputClass} hover:opacity-80 rounded-lg text-sm font-medium transition-colors border`}
            >
              {language === 'es' ? '🇬🇧 English' : '🇪🇸 Español'}
            </button>
          </div>

          {versions.map((version) => (
            <div key={version.id} className={`${cardClass} rounded-lg shadow-md p-6 border-l-4 border-blue-600`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-blue-600">{version.version}</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                    getReleaseTypeBadge(version.release_type)
                  }`}>
                    {version.release_type}
                  </span>
                  {version.is_active && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300 dark:bg-green-900 dark:text-green-200 dark:border-green-700">
                      Activa
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setExpandedChangelog(expandedChangelog === version.id ? null : version.id)}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {expandedChangelog === version.id ? 'Ocultar' : 'Ver'} Changelog
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                <div>
                  <span className={textSecondaryClass}>Fecha:</span>
                  <span className={`ml-2 font-medium ${textClass}`}>{formatDate(version.release_date)}</span>
                </div>
                <div>
                  <span className={textSecondaryClass}>Autor:</span>
                  <span className={`ml-2 font-medium ${textClass}`}>{version.author}</span>
                </div>
                <div>
                  <span className={textSecondaryClass}>Módulos:</span>
                  <span className={`ml-2 font-medium ${textClass}`}>{version.modules_affected.length}</span>
                </div>
                {version.git_commit_hash && (
                  <div>
                    <span className={textSecondaryClass}>Commit:</span>
                    <span className="ml-2 font-mono text-xs">{version.git_commit_hash.substring(0, 8)}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                {version.modules_affected.map((module, idx) => (
                  <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium border border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700">
                    {module}
                  </span>
                ))}
              </div>

              {expandedChangelog === version.id && (
                <div className={`mt-4 p-4 ${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg border ${borderClass}`}>
                  <pre className={`whitespace-pre-wrap text-sm ${textClass} font-sans`}>
                    {language === 'es' ? version.changelog_es : version.changelog_en}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Backups Tab */}
      {activeTab === 'backups' && (
        <div className="space-y-6">
          {/* Crear Nuevo Backup */}
          <div className={`${cardClass} rounded-lg shadow-md p-6 border`}>
            <h3 className={`text-lg font-bold ${textClass} mb-4 flex items-center gap-2`}>
              <Upload className="w-5 h-5 text-blue-600" />
              Crear Nuevo Backup
            </h3>
            <div className="flex flex-col md:flex-row gap-4">
              <select
                value={selectedBackupType}
                onChange={(e) => setSelectedBackupType(e.target.value)}
                className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${inputClass}`}
                disabled={isCreatingBackup}
              >
                <option value="full_db">Base de Datos Completa</option>
                <option value="incremental_db">Base de Datos Incremental (24h)</option>
                <option value="files">Solo Archivos</option>
                <option value="complete">Completo (DB + Archivos)</option>
              </select>
              <button
                onClick={createBackup}
                disabled={isCreatingBackup}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isCreatingBackup ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Creando...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Crear Backup
                  </>
                )}
              </button>
              <button
                onClick={cleanupExpiredBackups}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-5 h-5" />
                Limpiar Expirados
              </button>
            </div>
          </div>

          {/* Historial de Backups */}
          <div className={`${cardClass} rounded-lg shadow-md overflow-hidden border`}>
            <div className={`p-6 border-b ${borderClass}`}>
              <h3 className={`text-lg font-bold ${textClass} flex items-center gap-2`}>
                <Database className="w-5 h-5 text-blue-600" />
                Historial de Respaldos
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${textSecondaryClass} uppercase tracking-wider`}>Estado</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${textSecondaryClass} uppercase tracking-wider`}>Tipo</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${textSecondaryClass} uppercase tracking-wider`}>Versión</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${textSecondaryClass} uppercase tracking-wider`}>Tamaño</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${textSecondaryClass} uppercase tracking-wider`}>Fecha</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${textSecondaryClass} uppercase tracking-wider`}>Retención</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${textSecondaryClass} uppercase tracking-wider`}>Acciones</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${borderClass}`}>
                  {backups.map((backup) => (
                    <tr key={backup.id} className={`hover:${isDark ? 'bg-gray-700' : 'bg-gray-50'} transition-colors`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(backup.status)}
                          {backup.encryption_enabled && (
                            <Shield className="w-4 h-4 text-green-600" title="Encriptado" />
                          )}
                        </div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${textClass}`}>
                        {getBackupTypeLabel(backup.backup_type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium dark:bg-blue-900 dark:text-blue-200">
                          {backup.version}
                        </span>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${textClass}`}>
                        {formatFileSize(backup.file_size_mb)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${textSecondaryClass}`}>
                        {formatDate(backup.created_at)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${textSecondaryClass}`}>
                        {new Date(backup.retention_until).toLocaleDateString('es-CL')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => restoreBackup(backup.id, true)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="Validar backup"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => restoreBackup(backup.id, false)}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title="Restaurar backup"
                          >
                            <RotateCcw className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemHealth;